import React, { Component } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import ShoppingCartTotals from './ShoppingCartTotals';
import { Redirect } from 'react-router'
import { withTranslation } from 'react-i18next';
import {
    confirmPickUpLocation,
    submitOrder,
    confirmDeliveryLocation,
    getAddresses,
    confirmWithoutAddress,
    removeAllProductsFromShoppingCart,
    authModalToggle,
    toggleExpress,
    submitOrderAndGoToDetails
} from "../../store/actions";
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    CardFooter,
    Col, Modal,
    ModalHeader,
    ModalBody,
    Button,
    ModalFooter
} from 'reactstrap';
import AddressModal from "../auth/UserProfile/AddressesTab/AddressModal.js";
import { formatNumber } from '../../Utils';
import CheckoutPageStepBox from './CheckoutPageStepBox';
import {
    getDefaultPickupAddress,
    getDefaultDeliveryAddress,
    getBillingAddress
} from "../../store/reducers/addresses";
import Calendar from 'react-calendar';
import moment from "moment-business-days";
import { calendarDayStartMillis, zonedDateTimeMillis } from '../../utils/swissDateTime';
import ProductLoader from '../products/ProductLoader';
import { showOnlyPaymentOnCheckout } from '../../store/reducers/shoppingCart';
import i18next from 'i18next';
import {UncontrolledPopover, PopoverHeader, PopoverBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import SvgLightning from '../shared/SvgLightning.js';
moment.updateLocale('de', {
    holidays: [
        '26-05-2022',
        '06-06-2022',
        '01-08-2022',
        '25-12-2022',
        '26-12-2022',
        '01-01-2023',
        '07-04-2023',
        '10-04-2023',
        '01-05-2023',
        '18-05-2023',
        '29-05-2023',
        '01-08-2023',
        '25-12-2023',
        '26-12-2023',
        '01-05-2024',
        '09-05-2024',
        '20-05-2024',
        '01-08-2024',
        '25-12-2024',
        '26-12-2024',
        '01-01-2025',
        '01-05-2025',
        '29-05-2025',
        '09-06-2025',
        '01-08-2025',
        '25-12-2025',
        '26-12-2025',
        '01-01-2026',
        '03-04-2026',
        '06-04-2026',
        '01-05-2026',
        '14-05-2026',
        '25-05-2026',
        '01-08-2026',
        '25-12-2026',
        '26-12-2026',
    ],
    holidayFormat: 'DD-MM-YYYY',
    workingWeekdays: [1, 2, 3, 4, 5]
});

/**
 * Test date/time override for admin UI testing
 * This is set by the admin date/time picker in the UI
 */
let testDateTimeOverride = null;

/**
 * Gets the current date and time - can be overridden for testing
 * Override this function for testing:
 *   import CheckoutPage, { setCurrentDate } from './CheckoutPage';
 *   // Set date only (defaults to midnight):
 *   setCurrentDate(() => new Date('2024-01-15'));
 *   // Set date with specific time:
 *   setCurrentDate(() => new Date('2024-01-15T10:30:00'));
 *   // Or using Date constructor with time:
 *   setCurrentDate(() => new Date(2024, 0, 15, 10, 30)); // Jan 15, 2024, 10:30 AM
 * Or:
 *   CheckoutPage.setCurrentDate(() => new Date('2024-01-15T10:30:00'));
 * @returns {Date} Current date and time (or test date/time if overridden)
 */
let getCurrentDate = () => {
    // If admin has set a test date/time (sessionStorage + reload), use that
    if (testDateTimeOverride) {
        return new Date(testDateTimeOverride);
    }
    return new Date();
};

/**
 * Sets the current date and time function for testing
 * The date function should return a Date object that includes both date and time.
 * The time will be used to determine if same-day pickup is allowed (before/after 12:00 Zurich time).
 * 
 * Examples:
 *   // Set date with time using ISO string:
 *   setCurrentDate(() => new Date('2024-01-15T10:30:00')); // 10:30 AM
 *   setCurrentDate(() => new Date('2024-01-15T14:00:00')); // 2:00 PM (after noon)
 *   
 *   // Set date with time using Date constructor:
 *   setCurrentDate(() => new Date(2024, 0, 15, 10, 30)); // Jan 15, 2024, 10:30 AM
 *   setCurrentDate(() => new Date(2024, 0, 15, 14, 0));  // Jan 15, 2024, 2:00 PM
 * 
 * @param {Function} dateFn - Function that returns a Date object (with date and time)
 */
export const setCurrentDate = (dateFn) => {
    getCurrentDate = dateFn;
    // Clear UI override when programmatically setting
    testDateTimeOverride = null;
};

/**
 * Sets the test date/time from admin UI
 * @param {string|null} dateTimeString - ISO date/time string or null to clear
 */
export const setTestDateTime = (dateTimeString) => {
    testDateTimeOverride = dateTimeString;
};

/** Persists admin simulated "now" across reload (see checkout admin picker below `.checkout-out-page-wrapper`). */
export const CHECKOUT_ADMIN_TEST_DATETIME_KEY = 'woseu.checkout.adminTestDateTime';

const pad2 = (n) => String(n).padStart(2, '0');

/** Format a Date for `<input type="datetime-local" />` in the browser local timezone. */
export const formatDateTimeLocalInputValue = (d) => {
    if (!d || isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/**
 * Gets the current hour in Zurich timezone (Europe/Zurich)
 * Uses getCurrentDate() to allow date override for testing
 * @returns {number} Current hour (0-23) in Zurich timezone
 */
const getCurrentHourZurich = () => {
    const currentDate = getCurrentDate();
    const zurichTime = currentDate.toLocaleString('en-US', { timeZone: 'Europe/Zurich' });
    return new Date(zurichTime).getHours();
}

/**
 * Checks if same-day pickup is allowed
 * Same-day pickup is allowed if:
 * - Express service is enabled
 * - Current time in Zurich is before 12:00 (noon)
 * - Today is a business day
 * @param {boolean} isExpress - Whether express service is selected
 * @returns {boolean} True if same-day pickup is allowed
 */
export const isSameDayPickupAllowed = (isExpress) => {
    if (!isExpress) {
        return false;
    }
    const currentHour = getCurrentHourZurich();
    if (currentHour >= 12) {
        return false; // After 12:00, same-day pickup not allowed
    }
    const today = getCurrentDate(); // Use getCurrentDate() to allow date override for testing
    const todayMoment = moment(`${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`, 'DD-MM-YYYY');
    return todayMoment.isBusinessDay();
}

/**
 * Checks if a given date is today (ignoring time)
 * Uses getCurrentDate() to allow date override for testing
 * @param {Date} date - Date to check
 * @returns {boolean} True if the date is today
 */
const isToday = (date) => {
    const today = getCurrentDate(); // Use getCurrentDate() to allow date override for testing
    const checkDate = new Date(date);
    return today.getDate() === checkDate.getDate() &&
           today.getMonth() === checkDate.getMonth() &&
           today.getFullYear() === checkDate.getFullYear();
};

/**
 * Checks if a date would be express-only for pickup (today when same-day would be allowed with express)
 * Used to show and highlight today in the calendar when express is off
 * @param {Date} date - Date to check
 * @returns {boolean} True if the date is today and same-day pickup would be allowed with express
 */
export const isExpressOnlyPickupDate = (date) => {
    return isToday(date) && isSameDayPickupAllowed(true);
};

/**
 * A pickup slot is express when it falls on an express-only pickup day
 * and uses an afternoon slot.
 */
export const isExpressPickupSlot = (date, timeSlot) => {
    if (!date || !timeSlot) return false;
    return isExpressOnlyPickupDate(date) && !isMorningSlot(timeSlot);
};

/**
 * Checks if a time slot is a morning slot (before 13:00)
 * Morning slots: 07:00-09:00, 09:00-11:00, 11:00-13:00
 * @param {string} timeSlot - Time slot string (e.g., "07:00 - 09:00")
 * @returns {boolean} True if the time slot is a morning slot
 */
export const isMorningSlot = (timeSlot) => {
    const morningSlots = ['07:00 - 09:00', '09:00 - 11:00', '11:00 - 13:00'];
    return morningSlots.includes(timeSlot);
}

/**
 * Gets the first available afternoon slot
 * Afternoon slots: 13:00-15:00, 15:00-17:00, 17:00-19:00
 * @returns {string} First afternoon time slot
 */
export const getFirstAfternoonSlot = () => {
    return '13:00 - 15:00';
}

/**
 * Checks if a time slot requires 24-hour delivery for express orders
 * For express orders, late afternoon slots (15:00-17:00, 17:00-19:00) require exactly 24 hours later
 * @param {string} timeSlot - Time slot string (e.g., "15:00 - 17:00")
 * @returns {boolean} True if the time slot requires 24-hour delivery
 */
export const requires24HourDelivery = (timeSlot) => {
    const lateAfternoonSlots = ['15:00 - 17:00', '17:00 - 19:00'];
    return lateAfternoonSlots.includes(timeSlot);
}

/**
 * Calculates the earliest pickup date
 * If same-day pickup is allowed (express + before 12:00 Zurich time), returns today
 * Otherwise returns the next business day
 * Uses getCurrentDate() to allow date override for testing
 * @param {boolean} isExpress - Whether express service is selected
 * @returns {Date} Earliest pickup date
 */
export const getStartDate = (isExpress = false) => {
    const currentDate = getCurrentDate(); // Use getCurrentDate() to allow date override for testing
    const todayMoment = moment(`${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`, 'DD-MM-YYYY');
    
    // If same-day pickup is allowed, return today
    if (isSameDayPickupAllowed(isExpress)) {
        return todayMoment._d;
    }
    
    // Otherwise, return next business day
    return todayMoment.nextBusinessDay()._d;
}

const invoiceOnly = (userEmail = '') => {
    let isSpecialUser = false;
    try {
        const invoiceOnlyEmails = (process.env.REACT_APP_INVOICE_ONLY_EMAILS || '')
            .split(',')
            .map((email) => email.trim().toLowerCase())
            .filter(Boolean);
    
        const normalizedUserEmail = userEmail.trim().toLowerCase();
        isSpecialUser = invoiceOnlyEmails.includes(normalizedUserEmail) ? true : false;
    } catch (error) {
        console.error('Error parsing user email', error)
    }
    return isSpecialUser;
};

/**
 * Checks if any items in the cart have longProcessing
 * @param {Array} items - Array of cart items
 * @returns {boolean} True if any item has longProcessing
 */
export const hasLongProcessingItems = (items) => {
    if (!items || items.length === 0) {
        return false;
    }
    return items.some(item => item.longProcessing > 0);
}

/**
 * Checks if ALL items in the cart have longProcessing
 * @param {Array} items - Array of cart items
 * @returns {boolean} True if ALL items have longProcessing
 */
export const allItemsAreLongProcessing = (items) => {
    if (!items || items.length === 0) {
        return false;
    }
    return items.length > 0 && items.every(item => item.longProcessing > 0);
}

/**
 * Calculates the delivery date based on pickup date and service type
 * If pickup is same-day (today), delivery is next business day
 * Otherwise uses standard calculation
 * @param {Date} pickupDate - The selected pickup date
 * @param {number} minDeliveryDate - Minimum delivery days (1 for express, 2 for regular)
 * @param {boolean} isExpress - Whether express service is selected
 * @param {Array} items - Optional: cart items to check for longProcessing
 * @returns {Date} Calculated delivery date
 */
export const getDeliveryDate = (pickupDate, minDeliveryDate, isExpress, items = null) => {
    const pickupMoment = moment(`${pickupDate.getDate()}-${pickupDate.getMonth() + 1}-${pickupDate.getFullYear()}`, 'DD-MM-YYYY');
    
    // Check if ALL items are longProcessing
    const allLongProcessing = items ? allItemsAreLongProcessing(items) : false;
    
    // If pickup is same-day (today), delivery is next business day UNLESS all items are longProcessing
    if (isToday(pickupDate) && !allLongProcessing) {
        return pickupMoment.nextBusinessDay()._d;
    }
    
    // Standard calculation: start from pickup date and add business days
    if (allLongProcessing) {
        // ALL items are longProcessing: 14 business days for both regular and express
        minDeliveryDate = 14;
    } else if (!isExpress) {
        // Regular orders: 2 business days normally
        minDeliveryDate = 2;
    } else {
        // Express orders: 1 business day (when not all items are longProcessing)
        minDeliveryDate = 1;
    }
    return pickupMoment.businessAdd(minDeliveryDate)._d;
}

/**
 * A delivery slot is express when the selected delivery day is the express minimum
 * delivery day for the current pickup selection.
 */
export const isExpressDeliverySlot = (deliveryDate, pickUpDate, items = []) => {
    if (!deliveryDate || !pickUpDate) return false;
    if (allItemsAreLongProcessing(items || [])) return false;
    const expressMinDeliveryDate = getDeliveryDate(pickUpDate, null, true, items);
    return moment(deliveryDate).startOf('day').isSame(moment(expressMinDeliveryDate).startOf('day'), 'day');
};

export const shouldUseExpressForSelection = ({ pickUpDate, pickUpTime, deliveryDate, items = [] }) => {
    return isExpressPickupSlot(pickUpDate, pickUpTime) || isExpressDeliverySlot(deliveryDate, pickUpDate, items);
};

const TIME_SLOTS = ['07:00 - 09:00', '09:00 - 11:00', '11:00 - 13:00', '13:00 - 15:00', '15:00 - 17:00', '17:00 - 19:00'];

// TEMP: remove when late afternoon slots are re-enabled
const TEMPORARILY_DISABLED_SLOTS = ['15:00 - 17:00', '17:00 - 19:00'];
const isSlotTemporarilyDisabled = (slot) => TEMPORARILY_DISABLED_SLOTS.includes(slot);

const isSameDayAs = (a, b) => moment(a).startOf('day').isSame(moment(b).startOf('day'), 'day');

export const isBeforePickupTimeOnExpressMinDay = ({ pickUpDate, pickUpTime, deliveryDate, deliveryTime, items = [] }) => {
    if (!pickUpDate || !pickUpTime || !deliveryDate || !deliveryTime) return false;
    if (allItemsAreLongProcessing(items || [])) return false;
    const expressMinDeliveryDate = getDeliveryDate(pickUpDate, null, true, items);
    if (!isSameDayAs(deliveryDate, expressMinDeliveryDate)) return false;
    const pickupTimeIndex = TIME_SLOTS.indexOf(pickUpTime);
    const deliveryTimeIndex = TIME_SLOTS.indexOf(deliveryTime);
    if (pickupTimeIndex === -1 || deliveryTimeIndex === -1) return false;
    return deliveryTimeIndex < pickupTimeIndex;
};

export const getMinDeliveryDateForMode = ({ pickUpDate, pickUpTime, isExpressMode, items = [] }) => {
    const allLongProcessing = allItemsAreLongProcessing(items || []);
    const pickupIsToday = isToday(pickUpDate);
    const sameDayAllowed = isSameDayPickupAllowed(isExpressMode);
    const is24HourDelivery = isExpressMode && requires24HourDelivery(pickUpTime);

    if (is24HourDelivery) {
        const pickupMoment = moment(`${pickUpDate.getDate()}-${pickUpDate.getMonth() + 1}-${pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
        return pickupMoment.clone().add(1, 'days')._d;
    }

    if (pickupIsToday && sameDayAllowed && !allLongProcessing) {
        return getDeliveryDate(pickUpDate, isExpressMode ? 1 : 2, isExpressMode, items);
    }

    let effectiveMinDeliveryDate;
    if (allLongProcessing) {
        effectiveMinDeliveryDate = 14;
    } else if (isExpressMode) {
        effectiveMinDeliveryDate = 1;
    } else {
        effectiveMinDeliveryDate = 2;
    }

    let extraDay = 0;
    if (!isExpressMode && pickUpTime === '17:00 - 19:00') {
        extraDay = 1;
    }

    const pickupMoment = moment(`${pickUpDate.getDate()}-${pickUpDate.getMonth() + 1}-${pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
    return pickupMoment.businessAdd(effectiveMinDeliveryDate + extraDay)._d;
};



export class CheckoutPage extends Component {

    /**
     * Static method to get the current date function (for testing)
     * @returns {Function} The getCurrentDate function
     */
    static getCurrentDate = () => {
        return getCurrentDate();
    }

    /**
     * Static method to set the current date and time function (for testing)
     * Usage:
     *   // Set date only (defaults to midnight):
     *   CheckoutPage.setCurrentDate(() => new Date('2024-01-15'))
     *   // Set date with specific time:
     *   CheckoutPage.setCurrentDate(() => new Date('2024-01-15T10:30:00')) // 10:30 AM
     *   CheckoutPage.setCurrentDate(() => new Date('2024-01-15T14:00:00')) // 2:00 PM (after noon)
     *   // Or using Date constructor:
     *   CheckoutPage.setCurrentDate(() => new Date(2024, 0, 15, 10, 30)) // Jan 15, 2024, 10:30 AM
     * @param {Function} dateFn - Function that returns a Date object (with date and time)
     */
    static setCurrentDate = (dateFn) => {
        getCurrentDate = dateFn;
    }

    state = {
        showModal: false,
        addressForEdit: {},
        isCalendarOpen: false,
        date: getStartDate(this.props.isExpress),
        fromToTime: '07:00 - 09:00',
        currentFromToDateBeingEdited: '',
        // Draft date/time for the date/time modal: only updates on calendar/time click; real state updates only on time slot click
        modalDraftDate: null,
        modalDraftTime: '',
        // Initialize pickup date: if same-day allowed, use today with afternoon slot, otherwise next business day
        pickUpDate: getStartDate(this.props.isExpress),
        // Initialize delivery date based on pickup date (pass items to check for longProcessing)
        deliveryDate: getDeliveryDate(getStartDate(this.props.isExpress), this.props.minDeliveryDate, this.props.isExpress, this.props.shoppingCart?.items),
        // If same-day pickup is allowed, start with afternoon slot, otherwise morning slot
        pickUpTime: isSameDayPickupAllowed(this.props.isExpress) ? getFirstAfternoonSlot() : '07:00 - 09:00',
        // If pickup is same-day, delivery is next day and should be afternoon slot
        deliveryTime: isSameDayPickupAllowed(this.props.isExpress) ? getFirstAfternoonSlot() : '07:00 - 09:00',
        // Admin datetime-local: draft while editing; commit to sessionStorage + reload on blur only
        adminTestDateTimeDraft: null,
    };

    handleAdminTestDateTimeFocus = () => {
        if (this.state.adminTestDateTimeDraft !== null) {
            return;
        }
        let raw = null;
        try {
            raw = sessionStorage.getItem(CHECKOUT_ADMIN_TEST_DATETIME_KEY);
        } catch (e) {
            return;
        }
        let display = '';
        if (raw) {
            const parsed = new Date(raw);
            if (!isNaN(parsed.getTime())) {
                display = formatDateTimeLocalInputValue(parsed);
            }
        }
        this.setState({ adminTestDateTimeDraft: display });
    };

    handleAdminTestDateTimeInputChange = (e) => {
        this.setState({ adminTestDateTimeDraft: e.target.value });
    };

    handleAdminTestDateTimeBlur = (e) => {
        const v = e.target.value;
        let previousRaw = null;
        try {
            previousRaw = sessionStorage.getItem(CHECKOUT_ADMIN_TEST_DATETIME_KEY);
        } catch (err) {
            this.setState({ adminTestDateTimeDraft: null });
            return;
        }

        let previousDisplay = '';
        if (previousRaw) {
            const parsed = new Date(previousRaw);
            if (!isNaN(parsed.getTime())) {
                previousDisplay = formatDateTimeLocalInputValue(parsed);
            }
        }

        if (v === previousDisplay) {
            this.setState({ adminTestDateTimeDraft: null });
            return;
        }

        try {
            if (v) {
                sessionStorage.setItem(CHECKOUT_ADMIN_TEST_DATETIME_KEY, v);
            } else {
                sessionStorage.removeItem(CHECKOUT_ADMIN_TEST_DATETIME_KEY);
            }
        } catch (err) {
            this.setState({ adminTestDateTimeDraft: null });
            return;
        }
        this.setState({ adminTestDateTimeDraft: null });
        window.location.reload();
    };

    clearAdminTestDateTime = (e) => {
        if (e) {
            e.preventDefault();
        }
        try {
            sessionStorage.removeItem(CHECKOUT_ADMIN_TEST_DATETIME_KEY);
        } catch (err) {
            return;
        }
        window.location.reload();
    };

    componentDidMount() {
        const { token, OnGetAddresses, isAdmin } = this.props;
        
        // Admin: simulated "now" from sessionStorage (set via datetime picker under checkout; reload applies it)
        if (isAdmin) {
            let testDateTime = null;
            try {
                testDateTime = sessionStorage.getItem(CHECKOUT_ADMIN_TEST_DATETIME_KEY);
            } catch (e) {
                testDateTime = null;
            }
            if (testDateTime && !isNaN(new Date(testDateTime).getTime())) {
                setTestDateTime(testDateTime);

                // Recalculate pickup/delivery dates based on new current date
                const { isExpress, minDeliveryDate, shoppingCart } = this.props;
                const newPickupDate = getStartDate(isExpress);
                const newDeliveryDate = getDeliveryDate(newPickupDate, minDeliveryDate, isExpress, shoppingCart?.items);

                this.setState({
                    pickUpDate: newPickupDate,
                    deliveryDate: newDeliveryDate,
                    pickUpTime: isSameDayPickupAllowed(isExpress) ? getFirstAfternoonSlot() : '07:00 - 09:00',
                    deliveryTime: isSameDayPickupAllowed(isExpress) ? getFirstAfternoonSlot() : '07:00 - 09:00',
                });
            } else {
                if (testDateTime) {
                    try {
                        sessionStorage.removeItem(CHECKOUT_ADMIN_TEST_DATETIME_KEY);
                    } catch (e) { /* ignore */ }
                }
                setTestDateTime(null);
            }
        }
        
        window.removeEventListener('pageshow', this.handlePageShow);
        window.addEventListener('pageshow', this.handlePageShow);
        OnGetAddresses(token);
    }

    componentDidUpdate(prevProps) {
        if (this.props.paymentLink && prevProps.paymentLink !== this.props.paymentLink) {
            this.redirectToPayment(this.props.paymentLink);
        } else if (this.props.orderId && prevProps.orderId !== this.props.orderId) {
            this.setState( {loading: false} )
            window.location.assign(`/payengine?status=success&order=${this.props.orderId}`)
            return;
        } else if(this.props.isExpress !== prevProps.isExpress || this.props.minDeliveryDate !== prevProps.minDeliveryDate) {
            // While user is actively editing delivery date/time, never run express/minDelivery
            // recalculation that can override pickup selection.
            if (this.state.isCalendarOpen && this.state.currentFromToDateBeingEdited === 'deliveryDate') {
                return;
            }
            // Express/minDelivery changes should recompute delivery from the currently selected pickup slot,
            // not reset pickup to defaults.
            this.onDateChange('pickUpDate', this.state.pickUpDate, this.state.pickUpTime);
        }

    }

    getExpressPrice = () => {
        const { items } = this.props.shoppingCart;

        let totalPrice = 0;
        items.forEach(item => {
            const laundry = item.laundry;
            if (!laundry) return;

            if (laundry.noLaundrySelected) return;

            // Per-kilo must run before non-per-kilo speedy checks: calculatePrice uses washingPerKiloPrice
            // as the express add-on when washingAndIroningSpeedyPrice is 0, but an early speedyPrice<=0
            // return would skip this entirely and under-report vs actual item sums.
            if (laundry.perKilo) {
                const perKiloExpressPrice = laundry.washingAndIroningSpeedyPrice > 0
                    ? laundry.washingAndIroningSpeedyPrice
                    : laundry.washingPerKiloPrice;
                if (perKiloExpressPrice > 0) {
                    totalPrice += perKiloExpressPrice * (item.itemQuantity || 1);
                }
                return;
            }

            const speedyPrice = laundry.washingAndIroningSpeedyPrice || 0;
            if (speedyPrice <= 0) return;

            const washOrManualIronAddsExpressSurcharge =
                laundry.washingAndIroningSelected ||
                laundry.washingAndManualIroningSelected;

            if (washOrManualIronAddsExpressSurcharge) {
                totalPrice += speedyPrice * (item.itemQuantity || 1);
            } else if (
                laundry.washingAndIroningSpeedySelected &&
                !laundry.washingAndIroningSelected &&
                !laundry.washingAndManualIroningSelected
            ) {
                // Standalone "wash & iron express" option: premium is in item price but was omitted here,
                // so checkout could show e.g. +35 while totals implied +40.
                totalPrice += speedyPrice * (item.itemQuantity || 1);
            }
        });
        return totalPrice;
    }

    handlePageShow = (event) => {
        if (event.persisted) {
            this.setState({loading: false})
        }
    };

    editClickHandler = (e, address) => {
        this.setState({ showModal: true, addressForEdit: { ...address } })
    };

    modalToggleHandler = () => {
        this.setState({ showModal: false })
    }


    confirmDeliveryLocation = (value) => {
        this.props.confirmDeliveryLocation(value);
    };

    checkLoggedIn = () => {
        if (!this.props.token) {
            this.props.OnAuthModalToggle();
            // Trigger loggin modal
        }
    };

    handleOrderSubmit = () => {

        this.setState( {loading: true} )
        
        const { defaultPickupAddress, defaultDeliveryAddress, token, showOnlyPayment, onSubmitOrder, onSubmitOrderAndGoToDetails, shoppingCart, userName, isExpress } = this.props
        const { pickUpTime, deliveryTime, pickUpDate, deliveryDate } = this.state;

        const payload = {
            pickUpAddress: defaultPickupAddress,
            deliveryAddress: defaultDeliveryAddress,
            billingAddress: getBillingAddress,
            items: shoppingCart.items,
            welcomecode: shoppingCart.welcomecode,
            giftcard: shoppingCart.giftcard,
            paymentOnly: showOnlyPayment,
            codeUsed: shoppingCart.codeUsed,
            codeType: shoppingCart.codeType,
            codeValue: shoppingCart.codeValue,
            totalPrice: shoppingCart.totalPrice,
            token,
            isExpress
        };

        if (!shoppingCart.selfPickUp) {
            const [pickUpTimeFrom, pickUpTimeTo] = pickUpTime.split('-');
            payload.pickUpAddress.pickUpDate = calendarDayStartMillis(pickUpDate);
            payload.pickUpAddress.pickUpTimeFrom = zonedDateTimeMillis(pickUpDate, parseInt(pickUpTimeFrom, 10))
            payload.pickUpAddress.pickUpTimeTo = zonedDateTimeMillis(pickUpDate, parseInt(pickUpTimeTo, 10));
            payload.selfPickUp = false
        } else {
            payload.selfPickUp = true
        }

        if (!shoppingCart.selfDelivery) {
            const [deliveryTimeFrom, deliveryTimeTo] = deliveryTime.split('-');
            payload.deliveryAddress.deliveryDate = calendarDayStartMillis(deliveryDate);
            payload.deliveryAddress.deliveryTimeFrom = zonedDateTimeMillis(deliveryDate, parseInt(deliveryTimeFrom, 10))
            payload.deliveryAddress.deliveryTimeTo = zonedDateTimeMillis(deliveryDate, parseInt(deliveryTimeTo, 10))
            payload.selfDelivery = false
        } else {
            payload.selfDelivery = true
        }
        if (!(shoppingCart.codeUsed && shoppingCart.totalPrice === 0)) {
            // Create an order intent on ConcardIs.
            this.state.payload = payload
            onSubmitOrder(payload); // this triggers onSubmitOrderSuccess which redirects to the payment page
        } else {
            this.setState({loading: false})
            onSubmitOrderAndGoToDetails(payload);
        }
    };

    redirectToPayment = (paymentLink) => {
        window.location.href = paymentLink   
    }

    onUpdateFromToDate = (name, e) => {
        const modalDraftDate = name === 'pickUpDate' ? this.state.pickUpDate : this.state.deliveryDate;
        const modalDraftTime = name === 'pickUpDate' ? this.state.pickUpTime : this.state.deliveryTime;
        this.setState({
            currentFromToDateBeingEdited: name,
            modalDraftDate,
            modalDraftTime,
            isCalendarOpen: true
        });
    };

    toggleDateTimePicker = () => {
        this.setState({ isCalendarOpen: !this.state.isCalendarOpen })
    };

    onChangeExpress = (e) => {
       this.props.onToggleExpress('manual')
    }

    getAutoTimeSlotForDate = (currentFromToDateBeingEdited, date, isExpressForCalculation = this.props.isExpress) => {
        const slots = TIME_SLOTS;

        const isEditingPickup = currentFromToDateBeingEdited === 'pickUpDate';
        const isEditingDelivery = currentFromToDateBeingEdited === 'deliveryDate';
        const isSaturday = date.getDay() === 6;
        const pickupIsToday = isToday(this.state.pickUpDate);
        const sameDayAllowed = isSameDayPickupAllowed(isExpressForCalculation);

        return slots.find((slot) => {
            if (isSlotTemporarilyDisabled(slot) || (isSaturday && (slot === '15:00 - 17:00' || slot === '17:00 - 19:00'))) {
                return false;
            }

            if (isEditingPickup) {
                if (isToday(date) && isMorningSlot(slot)) {
                    // Morning slots are hidden for same-day pickup in UI.
                    return false;
                }
                return true;
            }

            if (isEditingDelivery) {
                if (isExpressForCalculation && isBeforePickupTimeOnExpressMinDay({
                    pickUpDate: this.state.pickUpDate,
                    pickUpTime: this.state.pickUpTime,
                    deliveryDate: date,
                    deliveryTime: slot,
                    items: this.props.shoppingCart?.items || []
                })) {
                    return false;
                }

                if (pickupIsToday && sameDayAllowed && isMorningSlot(slot)) {
                    const minDeliveryDate = getDeliveryDate(this.state.pickUpDate, null, isExpressForCalculation, this.props.shoppingCart?.items);
                    if (moment(date).startOf('day').isSame(moment(minDeliveryDate).startOf('day'), 'day')) {
                        return false;
                    }
                }
                return true;
            }

            return true;
        }) || '13:00 - 15:00';
    };

    onCalendarDateChange = (date) => {
        const { currentFromToDateBeingEdited, pickUpDate, pickUpTime, deliveryDate } = this.state;
        const nextPickUpDate = currentFromToDateBeingEdited === 'pickUpDate' ? date : pickUpDate;
        const nextDeliveryDate = currentFromToDateBeingEdited === 'deliveryDate' ? date : deliveryDate;
        const shouldUseExpressPreAuto = currentFromToDateBeingEdited === 'deliveryDate'
            ? shouldUseExpressForSelection({
                pickUpDate: nextPickUpDate,
                pickUpTime: pickUpTime,
                deliveryDate: nextDeliveryDate,
                items: this.props.shoppingCart?.items || []
            })
            : this.props.isExpress;
        const autoTime = this.getAutoTimeSlotForDate(currentFromToDateBeingEdited, date, shouldUseExpressPreAuto);
        const nextPickUpTime = currentFromToDateBeingEdited === 'pickUpDate' ? autoTime : pickUpTime;
        const shouldUseExpress = currentFromToDateBeingEdited === 'pickUpDate'
            ? isExpressPickupSlot(nextPickUpDate, nextPickUpTime)
            : shouldUseExpressPreAuto;

        this.setState({
            modalDraftDate: date,
            modalDraftTime: autoTime
        });

        if (shouldUseExpress !== this.props.isExpress) {
            this.props.onToggleExpress('slotSelection');
        }

        this.onDateChange(currentFromToDateBeingEdited, date, autoTime);
    };

    onDateChange = (currentFromToDateBeingEdited, date, time) => {
        // Invoked only from setTime (date + time) or componentDidUpdate (switching to regular). Calendar no longer calls this.
        const timeExplicitlyPassed = time !== undefined;
        const isExpress = this.props.isExpress;

        if (currentFromToDateBeingEdited === 'pickUpDate') {
            const { minDeliveryDate, shoppingCart } = this.props;
            if (!time) {
                time = this.state.pickUpTime
            }
            const isExpressFromPickup = isExpressPickupSlot(date, time);

            // Check if pickup is same-day and validate time slot restrictions
            const pickupIsToday = isToday(date);
            const sameDayAllowed = isSameDayPickupAllowed(isExpressFromPickup);
            
            // Only auto-adjust morning to afternoon when user explicitly clicked a time slot; on date-only click keep current time
            if (timeExplicitlyPassed && pickupIsToday && sameDayAllowed && isMorningSlot(time)) {
                time = getFirstAfternoonSlot();
            }

            // For express orders with late afternoon slots (15:00-17:00, 17:00-19:00), 
            // delivery must be exactly 24 hours later (same time slot next day)
            const is24HourDelivery = isExpressFromPickup && requires24HourDelivery(time);
            
            // Check if ALL items are longProcessing
            const allLongProcessing = allItemsAreLongProcessing(shoppingCart.items);
            
            // Calculate effective minDeliveryDate based on longProcessing
            let effectiveMinDeliveryDate = minDeliveryDate;
            if (allLongProcessing) {
                // ALL longProcessing: 14 business days for both regular and express
                effectiveMinDeliveryDate = 14;
            } else if (isExpressFromPickup) {
                // Express with mixed/no longProcessing: 1 day (warning shown if hasLongProcessing)
                effectiveMinDeliveryDate = 1;
            } else {
                // Regular with mixed or no longProcessing: 2 days (normal)
                effectiveMinDeliveryDate = 2;
            }
            
            let addDay = 0;
            // Note: The old rule for 17:00-19:00 adding an extra day is replaced by the 24-hour rule
            // Only apply old rule if not using 24-hour delivery
            // For regular orders, effectiveMinDeliveryDate is already 2, so we don't need to add extra days
            if (time === '17:00 - 19:00' && !is24HourDelivery && !isExpressFromPickup) {
                addDay = 1;
            }
            
            // Calculate delivery date
            let businessDay;
            if (pickupIsToday && sameDayAllowed) {
                // Same-day pickup: delivery is always next business day
                // UNLESS it's express with 24-hour delivery rule
                if (is24HourDelivery) {
                    // For 24-hour delivery, add exactly 1 calendar day (24 hours)
                    const pickupMoment = moment(`${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`, 'DD-MM-YYYY');
                    businessDay = pickupMoment.add(1, 'days')._d;
                } else {
                    businessDay = getDeliveryDate(date, effectiveMinDeliveryDate, isExpressFromPickup, shoppingCart.items);
                }
            } else {
                // Standard calculation
                if (is24HourDelivery) {
                    // For express with 24-hour delivery, add exactly 1 calendar day (24 hours)
                    const pickupMoment = moment(`${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`, 'DD-MM-YYYY');
                    businessDay = pickupMoment.add(1, 'days')._d;
                } else {
                    // Standard calculation: add business days
                    // For ALL longProcessing, use 14 days (both regular and express)
                    if (allLongProcessing) {
                        const pickupMoment = moment(`${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`, 'DD-MM-YYYY');
                        businessDay = pickupMoment.businessAdd(14)._d;
                    } else {
                        const pickupMoment = moment(`${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`, 'DD-MM-YYYY');
                        businessDay = pickupMoment.businessAdd(effectiveMinDeliveryDate + addDay)._d;
                    }
                }
            }
            
            // Determine delivery time - when pickup changes, re-base delivery time to the earliest valid slot
            // for the newly computed delivery date/mode.
            let newDeliveryTime = this.state.deliveryTime;
            
            if (timeExplicitlyPassed) {
                if (is24HourDelivery) {
                    newDeliveryTime = time;
                } else if (pickupIsToday && sameDayAllowed) {
                    // Same-day pickup flow keeps delivery on earliest afternoon slot.
                    newDeliveryTime = getFirstAfternoonSlot();
                } else {
                    // Regular/non-same-day flow should use earliest morning slot.
                    newDeliveryTime = '07:00 - 09:00';
                }
            }
            
            // Pickup change always re-bases delivery to the earliest valid delivery for this pickup selection.
            this.setState({
                deliveryDate: businessDay,
                pickUpDate: date,
                pickUpTime: timeExplicitlyPassed ? this.checkIfTimeIsOk(date, time, pickupIsToday) : time,
                deliveryTime: timeExplicitlyPassed ? this.checkIfTimeIsOk(businessDay, newDeliveryTime, pickupIsToday) : this.state.deliveryTime
            });
        } else {
            if (!time) {
                time = this.state.deliveryTime
            }
            const isExpressForCalculation = shouldUseExpressForSelection({
                pickUpDate: this.state.pickUpDate,
                pickUpTime: this.state.pickUpTime,
                deliveryDate: date,
                items: this.props.shoppingCart?.items || []
            });
            
            // Check if 24-hour delivery rule applies
            const is24HourDelivery = isExpressForCalculation && requires24HourDelivery(this.state.pickUpTime);
            
            // Check if pickup was same-day - if so, delivery must use afternoon slots
            const pickupIsToday = isToday(this.state.pickUpDate);
            const sameDayAllowed = isSameDayPickupAllowed(isExpressForCalculation);
            
            // Calculate the minimum delivery date based on pickup date/time
            const { minDeliveryDate, shoppingCart } = this.props;
            const hasLongProcessing = hasLongProcessingItems(shoppingCart?.items || []);
            const allLongProcessing = allItemsAreLongProcessing(shoppingCart?.items || []);
            
            // Calculate effective minDeliveryDate based on longProcessing
            let effectiveMinDeliveryDate = minDeliveryDate;
            if (allLongProcessing) {
                effectiveMinDeliveryDate = 14; // ALL longProcessing: 14 days for both regular and express
            } else if (isExpressForCalculation) {
                effectiveMinDeliveryDate = 1; // Express with mixed/no longProcessing: 1 day
            } else {
                effectiveMinDeliveryDate = 2; // Regular with mixed or no longProcessing: 2 days
            }
            
            let addDay = 0;
            // Only add extra day for regular orders with 17:00-19:00 time slot (legacy rule)
            if (this.state.pickUpTime === '17:00 - 19:00' && !is24HourDelivery && !isExpressForCalculation) {
                addDay = 1;
            }
            
            // Calculate minimum delivery date
            let minDeliveryDateCalc;
            if (is24HourDelivery) {
                const pickupMoment = moment(`${this.state.pickUpDate.getDate()}-${this.state.pickUpDate.getMonth() + 1}-${this.state.pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
                minDeliveryDateCalc = pickupMoment.clone().add(1, 'days')._d;
            } else if (pickupIsToday && sameDayAllowed) {
                minDeliveryDateCalc = getDeliveryDate(this.state.pickUpDate, effectiveMinDeliveryDate, isExpressForCalculation, shoppingCart.items);
            } else {
                if (allLongProcessing) {
                    const pickupMoment = moment(`${this.state.pickUpDate.getDate()}-${this.state.pickUpDate.getMonth() + 1}-${this.state.pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
                    minDeliveryDateCalc = pickupMoment.businessAdd(14)._d;
                } else {
                    const pickupMoment = moment(`${this.state.pickUpDate.getDate()}-${this.state.pickUpDate.getMonth() + 1}-${this.state.pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
                    minDeliveryDateCalc = pickupMoment.businessAdd(effectiveMinDeliveryDate + addDay)._d;
                }
            }
            
            // Ensure delivery date is not before minimum - if it is, set to minimum
            let finalDeliveryDate = new Date(date.getTime());
            const dateMoment = moment(`${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`, 'DD-MM-YYYY');
            const minMoment = moment(`${minDeliveryDateCalc.getDate()}-${minDeliveryDateCalc.getMonth() + 1}-${minDeliveryDateCalc.getFullYear()}`, 'DD-MM-YYYY');
            
            if (dateMoment.isBefore(minMoment, 'day')) {
                // If selected date is before minimum, set to minimum
                finalDeliveryDate = new Date(minDeliveryDateCalc.getTime());
            }
            
            let finalDeliveryTime = time;
            
            // Only adjust delivery time when user explicitly clicked a time slot, not on date-only click
            if (timeExplicitlyPassed) {
                // Calculate minimum delivery date (next business day after pickup)
                const pickupMoment = moment(this.state.pickUpDate).startOf('day');
                const minDeliveryDateMoment = getDeliveryDate(pickupMoment.toDate(), null, isExpressForCalculation, shoppingCart.items);
                const minDeliveryMoment = moment(minDeliveryDateMoment).startOf('day');
                const selectedDateMoment = moment(date).startOf('day');
                const isMinimumDeliveryDate = selectedDateMoment.isSame(minDeliveryMoment, 'day');
                
                if (isExpressForCalculation && isBeforePickupTimeOnExpressMinDay({
                    pickUpDate: this.state.pickUpDate,
                    pickUpTime: this.state.pickUpTime,
                    deliveryDate: date,
                    deliveryTime: time,
                    items: shoppingCart?.items || []
                })) {
                    finalDeliveryTime = this.state.pickUpTime;
                } else if (pickupIsToday && sameDayAllowed && isMinimumDeliveryDate && isMorningSlot(time)) {
                    finalDeliveryTime = getFirstAfternoonSlot();
                }
            }
            
            this.setState({
                deliveryDate: finalDeliveryDate,
                deliveryTime: timeExplicitlyPassed ? this.checkIfTimeIsOk(finalDeliveryDate, finalDeliveryTime, pickupIsToday) : this.state.deliveryTime
            });
        }
    }

    /**
     * Validates and adjusts time slots based on date restrictions
     * - Saturday restrictions: disables late afternoon slots
     * - Same-day pickup restrictions: only afternoon slots allowed for pickup
     * - Same-day pickup delivery restrictions: only afternoon slots allowed for delivery when pickup was same-day
     * @param {Date} date - The date to check
     * @param {string} timePeriod - The time slot to validate
     * @param {boolean} pickupWasSameDay - Whether the pickup was same-day (for delivery time validation)
     * @returns {string} Validated/adjusted time slot
     */
    checkIfTimeIsOk = (date, timePeriod, pickupWasSameDay = false) => {
        // TEMP: disable late afternoon slots (15:00-17:00, 17:00-19:00) on all days
        if (isSlotTemporarilyDisabled(timePeriod)) {
            return '13:00 - 15:00';
        }
        // Saturday restrictions: disable late afternoon slots (15:00-17:00, 17:00-19:00)
        if (moment(`${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`, 'DD-MM-YYYY').isoWeekday() === 6) {
            const disabledTimes = ['17:00 - 19:00', '15:00 - 17:00']
            if (disabledTimes.includes(timePeriod)) {
                return '13:00 - 15:00'
            }
        }
        
        // Same-day pickup restrictions: if pickup is today and express, only afternoon slots allowed
        const pickupIsToday = isToday(date);
        const sameDayAllowed = isSameDayPickupAllowed(this.props.isExpress);
        
        if (pickupIsToday && sameDayAllowed && isMorningSlot(timePeriod)) {
            // Same-day pickup: morning slots not allowed, return first afternoon slot
            return getFirstAfternoonSlot();
        }
        
        // Same-day pickup delivery restrictions: if pickup was same-day, delivery must be afternoon slot
        // BUT only if delivery date is the minimum date (next business day)
        // For delivery dates AFTER the minimum, all time slots are allowed
        if (pickupWasSameDay && sameDayAllowed && isMorningSlot(timePeriod)) {
            // Check if the delivery date is the minimum date (next business day after pickup)
            // Only apply morning slot restriction if delivery is on the minimum date
            const pickupDate = this.state.pickUpDate;
            const deliveryDate = date; // date parameter is the delivery date when pickupWasSameDay is true
            
            // Calculate minimum delivery date (next business day after pickup)
            const pickupMoment = moment(pickupDate).startOf('day');
            const minDeliveryDateMoment = getDeliveryDate(pickupMoment.toDate(), null, this.props.isExpress);
            const minDeliveryMoment = moment(minDeliveryDateMoment).startOf('day');
            const deliveryMoment = moment(deliveryDate).startOf('day');
            const isMinimumDeliveryDate = deliveryMoment.isSame(minDeliveryMoment, 'day');
            
            // Only block morning slots if delivery date is the minimum date
            if (isMinimumDeliveryDate) {
                // Delivery after same-day pickup on minimum date: morning slots not allowed, return first afternoon slot
                return getFirstAfternoonSlot();
            }
            // For delivery dates after minimum, allow all time slots (including morning)
        }
        
        return timePeriod
    }

    /**
     * Returns true if the given time slot is express-only (shown when express is off; selecting it enables express).
     * Uses the date currently selected in the modal (modalDraftDate) so clicking a slot on an express-only day toggles express.
     */
    isExpressOnlySlot = (timeSlot) => {
        const { currentFromToDateBeingEdited, modalDraftDate, pickUpDate, deliveryDate } = this.state;
        const { isExpress, shoppingCart } = this.props;
        if (isExpress) return false;
        const isEditingPickup = currentFromToDateBeingEdited === 'pickUpDate';
        const isEditingDelivery = currentFromToDateBeingEdited === 'deliveryDate';
        const dateInModal = modalDraftDate ?? (isEditingPickup ? pickUpDate : deliveryDate);
        if (isEditingPickup) {
            const pickupIsToday = isToday(dateInModal);
            return pickupIsToday && !isMorningSlot(timeSlot);
        }
        if (isEditingDelivery) {
            const allLongProcessingCart = allItemsAreLongProcessing(shoppingCart?.items || []);
            if (allLongProcessingCart) return false;
            const expressMinDeliveryDate = getDeliveryDate(pickUpDate, null, true, shoppingCart.items);
            const isDeliveryDateExpressMin = dateInModal && moment(dateInModal).startOf('day').isSame(moment(expressMinDeliveryDate).startOf('day'), 'day');
            return isDeliveryDateExpressMin;
        }
        return false;
    };

    setTime = e => {
        const { currentFromToDateBeingEdited, modalDraftDate, pickUpDate, deliveryDate } = this.state;
        const timeSlot = e.target.id;
        const dateToApply = modalDraftDate ?? (currentFromToDateBeingEdited === 'pickUpDate' ? pickUpDate : deliveryDate);
        const nextPickUpDate = currentFromToDateBeingEdited === 'pickUpDate' ? dateToApply : pickUpDate;
        const nextPickUpTime = currentFromToDateBeingEdited === 'pickUpDate' ? timeSlot : this.state.pickUpTime;
        const nextDeliveryDate = currentFromToDateBeingEdited === 'deliveryDate' ? dateToApply : deliveryDate;
        const shouldUseExpress = currentFromToDateBeingEdited === 'pickUpDate'
            ? isExpressPickupSlot(nextPickUpDate, nextPickUpTime)
            : shouldUseExpressForSelection({
                pickUpDate: nextPickUpDate,
                pickUpTime: nextPickUpTime,
                deliveryDate: nextDeliveryDate,
                items: this.props.shoppingCart?.items || []
            });

        if (shouldUseExpress !== this.props.isExpress) {
            this.props.onToggleExpress('slotSelection');
        }

        this.onDateChange(currentFromToDateBeingEdited, dateToApply, timeSlot);
        this.setState({ modalDraftTime: timeSlot });
    };


    onDateModalSubmit = () => {
        this.toggleDateTimePicker();
    };

    /**
     * Calculates the earliest possible pickup date and time
     * @returns {Object} { date: Date, time: string }
     */
    getEarliestPickup = () => {
        const earliestDate = getStartDate(this.props.isExpress);
        const sameDayAllowed = isSameDayPickupAllowed(this.props.isExpress);
        const earliestTime = sameDayAllowed ? getFirstAfternoonSlot() : '07:00 - 09:00';
        return { date: earliestDate, time: earliestTime };
    };

    /**
     * Calculates the earliest possible pickup date and time WITH express service
     * @returns {Object} { date: Date, time: string }
     */
    getEarliestPickupWithExpress = () => {
        const earliestDate = getStartDate(true); // Always use express
        const sameDayAllowed = isSameDayPickupAllowed(true); // Always use express
        const earliestTime = sameDayAllowed ? getFirstAfternoonSlot() : '07:00 - 09:00';
        return { date: earliestDate, time: earliestTime };
    };

    /**
     * Earliest pickup without express — for display next to express earliest regardless of current toggle.
     */
    getEarliestPickupNormal = () => {
        const earliestDate = getStartDate(false);
        const sameDayAllowed = isSameDayPickupAllowed(false);
        const earliestTime = sameDayAllowed ? getFirstAfternoonSlot() : '07:00 - 09:00';
        return { date: earliestDate, time: earliestTime };
    };

    /**
     * Calculates the earliest possible delivery date and time based on current pickup
     * @returns {Object} { date: Date, time: string }
     */
    getEarliestDelivery = () => {
        const { minDeliveryDate, shoppingCart } = this.props;
        const { pickUpDate, pickUpTime } = this.state;
        
        const pickupIsToday = isToday(pickUpDate);
        const sameDayAllowed = isSameDayPickupAllowed(this.props.isExpress);
        const is24HourDelivery = this.props.isExpress && requires24HourDelivery(pickUpTime);
        const hasLongProcessing = hasLongProcessingItems(shoppingCart?.items || []);
        const allLongProcessing = allItemsAreLongProcessing(shoppingCart?.items || []);
        
        // Calculate effective minDeliveryDate
        let effectiveMinDeliveryDate = minDeliveryDate;
        if (allLongProcessing) {
            effectiveMinDeliveryDate = 14; // ALL longProcessing: 14 days for both regular and express
        } else if (this.props.isExpress) {
            effectiveMinDeliveryDate = 1; // Express with mixed/no longProcessing: 1 day
        } else {
            effectiveMinDeliveryDate = 2; // Regular with mixed or no longProcessing: 2 days
        }
        
        let addDay = 0;
        // Only add extra day for regular orders with 17:00-19:00 time slot (legacy rule)
        if (pickUpTime === '17:00 - 19:00' && !is24HourDelivery && !this.props.isExpress) {
            addDay = 1;
        }
        
        // Calculate earliest delivery date
        let earliestDeliveryDate;
        if (is24HourDelivery) {
            const pickupMoment = moment(`${pickUpDate.getDate()}-${pickUpDate.getMonth() + 1}-${pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
            earliestDeliveryDate = pickupMoment.clone().add(1, 'days')._d;
        } else if (pickupIsToday && sameDayAllowed) {
            earliestDeliveryDate = getDeliveryDate(pickUpDate, effectiveMinDeliveryDate, this.props.isExpress, shoppingCart.items);
        } else {
            if (allLongProcessing) {
                const pickupMoment = moment(`${pickUpDate.getDate()}-${pickUpDate.getMonth() + 1}-${pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
                earliestDeliveryDate = pickupMoment.businessAdd(14)._d;
            } else {
                const pickupMoment = moment(`${pickUpDate.getDate()}-${pickUpDate.getMonth() + 1}-${pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
                earliestDeliveryDate = pickupMoment.businessAdd(effectiveMinDeliveryDate + addDay)._d;
            }
        }
        
        // Calculate earliest delivery time
        let earliestDeliveryTime;
        if (is24HourDelivery) {
            earliestDeliveryTime = pickUpTime;
        } else if (pickupIsToday && sameDayAllowed) {
            earliestDeliveryTime = getFirstAfternoonSlot();
        } else {
            earliestDeliveryTime = '07:00 - 09:00';
        }
        
        return { date: earliestDeliveryDate, time: earliestDeliveryTime };
    };

    /**
     * Earliest delivery assuming non-express rules — for display alongside express earliest.
     */
    getEarliestDeliveryNormal = () => {
        const { shoppingCart } = this.props;
        const { pickUpDate, pickUpTime } = this.state;

        const pickupIsToday = isToday(pickUpDate);
        const sameDayAllowed = isSameDayPickupAllowed(false);
        const allLongProcessing = allItemsAreLongProcessing(shoppingCart?.items || []);

        const effectiveMinDeliveryDate = allLongProcessing ? 14 : 2;
        const addDay = pickUpTime === '17:00 - 19:00' ? 1 : 0;

        let earliestDeliveryDate;
        if (pickupIsToday && sameDayAllowed) {
            earliestDeliveryDate = getDeliveryDate(pickUpDate, effectiveMinDeliveryDate, false, shoppingCart.items);
        } else if (allLongProcessing) {
            const pickupMoment = moment(`${pickUpDate.getDate()}-${pickUpDate.getMonth() + 1}-${pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
            earliestDeliveryDate = pickupMoment.businessAdd(14)._d;
        } else {
            const pickupMoment = moment(`${pickUpDate.getDate()}-${pickUpDate.getMonth() + 1}-${pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
            earliestDeliveryDate = pickupMoment.businessAdd(effectiveMinDeliveryDate + addDay)._d;
        }

        const earliestDeliveryTime =
            pickupIsToday && sameDayAllowed ? getFirstAfternoonSlot() : '07:00 - 09:00';

        return { date: earliestDeliveryDate, time: earliestDeliveryTime };
    };

    /**
     * Calculates the earliest possible delivery date and time WITH express service
     * Based on current pickup date/time, but assuming express is enabled
     * @returns {Object} { date: Date, time: string }
     */
    getEarliestDeliveryWithExpress = () => {
        const { shoppingCart } = this.props;
        const { pickUpDate, pickUpTime } = this.state;
        
        const pickupIsToday = isToday(pickUpDate);
        const sameDayAllowed = isSameDayPickupAllowed(true); // Always use express
        const is24HourDelivery = requires24HourDelivery(pickUpTime); // Express always enabled
        const hasLongProcessing = hasLongProcessingItems(shoppingCart?.items || []);
        const allLongProcessing = allItemsAreLongProcessing(shoppingCart?.items || []);
        
        // With express: 14 days if ALL items are longProcessing, otherwise 1 day
        const effectiveMinDeliveryDate = allLongProcessing ? 14 : 1;
        
        // Calculate earliest delivery date
        let earliestDeliveryDate;
        if (is24HourDelivery) {
            const pickupMoment = moment(`${pickUpDate.getDate()}-${pickUpDate.getMonth() + 1}-${pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
            earliestDeliveryDate = pickupMoment.clone().add(1, 'days')._d;
        } else if (pickupIsToday && sameDayAllowed) {
            earliestDeliveryDate = getDeliveryDate(pickUpDate, effectiveMinDeliveryDate, true, shoppingCart.items); // Always express
        } else {
            const pickupMoment = moment(`${pickUpDate.getDate()}-${pickUpDate.getMonth() + 1}-${pickUpDate.getFullYear()}`, 'DD-MM-YYYY');
            earliestDeliveryDate = pickupMoment.businessAdd(effectiveMinDeliveryDate)._d;
        }
        
        // Calculate earliest delivery time
        let earliestDeliveryTime;
        if (is24HourDelivery) {
            earliestDeliveryTime = pickUpTime;
        } else if (pickupIsToday && sameDayAllowed) {
            earliestDeliveryTime = getFirstAfternoonSlot();
        } else {
            earliestDeliveryTime = '07:00 - 09:00';
        }
        
        return { date: earliestDeliveryDate, time: earliestDeliveryTime };
    };

    /**
     * Returns optional CSS class for calendar tiles that are express-only (shown when express is off)
     * @param {Date} date - Tile date
     * @param {string} view - Calendar view (month, year, etc.)
     * @returns {string|null} Class name or null
     */
    getTileClassName = ({ date, view }) => {
        if (view !== 'month') return null;
        const { currentFromToDateBeingEdited, pickUpDate, deliveryDate } = this.state;
        const pickupSlotIsExpress = isExpressPickupSlot(pickUpDate, this.state.pickUpTime);
        const realChosenDate = currentFromToDateBeingEdited === 'pickUpDate' ? pickUpDate : deliveryDate;
        const classes = [];
        const isPickup = currentFromToDateBeingEdited === 'pickUpDate';
        if (isPickup) {
            if (isExpressOnlyPickupDate(date)) classes.push('react-calendar__tile--express-option');
        } else {
            if (!pickupSlotIsExpress) {
                const { shoppingCart } = this.props;
                if (!allItemsAreLongProcessing(shoppingCart?.items || [])) {
                    const expressMinDate = getDeliveryDate(pickUpDate, null, true, shoppingCart.items);
                    if (moment(date).startOf('day').isSame(moment(expressMinDate).startOf('day'), 'day')) {
                        classes.push('react-calendar__tile--express-option');
                    }
                }
            }
        }
        return classes.length ? classes.join(' ') : null;
    };

    /**
     * Determines which pickup dates should be disabled in the calendar
     * Allows today's date if same-day pickup is allowed (express + before 12:00 Zurich time)
     * @param {Date} activeStartDate - Active start date from calendar
     * @param {Date} date - Date to check
     * @returns {boolean} True if the date should be disabled
     */
    disabledPickupDates = (activeStartDate, date) => {
        const { minDeliveryDate } = this.props;
        // Use getCurrentDate() to allow date override for testing
        const currentDate = getCurrentDate();
        const currentMoment = moment(`${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`, 'DD-MM-YYYY');
        const maxDate = currentMoment.clone().add(1, 'months').toDate();
        const daysDisabled = moment(`${maxDate.getDate()}-${maxDate.getMonth() + 1}-${maxDate.getFullYear()}`, 'DD-MM-YYYY').businessAdd(-minDeliveryDate)._d
        
        // Check if date is not a business day
        const isNotBusinessDay = !(moment(`${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`, 'DD-MM-YYYY').isBusinessDay());
        
        // Check if date is before today (use getCurrentDate() to allow date override for testing)
        const isBeforeToday = date < currentMoment.startOf('day').toDate();
        
        // Check if date is beyond maximum booking window
        const isBeyondMaxDate = date > daysDisabled;
        
        // Check if date is today
        const isTodayDate = isToday(date);
        
        // Allow today when same-day pickup would be allowed with express (before 12:00 Zurich, business day).
        // When express is off, today is still selectable and shown as express option (tileClassName).
        const allowToday = isTodayDate && isSameDayPickupAllowed(true);
        
        // Disable if: not business day OR before today OR (today AND not allowed) OR beyond max date
        return isNotBusinessDay || isBeforeToday || (isTodayDate && !allowToday) || isBeyondMaxDate;
    };

    /**
     * Determines which delivery dates should be disabled in the calendar
     * Accounts for 24-hour delivery rule for express orders with late afternoon slots
     * @param {Date} activeStartDate - Active start date from calendar
     * @param {Date} date - Date to check
     * @returns {boolean} True if the date should be disabled
     */
    disabledDeliveryDate = (activeStartDate, date) => {
        const { pickUpDate, pickUpTime } = this.state
        const { minDeliveryDate, shoppingCart } = this.props;
        const allLongProcessing = allItemsAreLongProcessing(shoppingCart?.items || []);
        const minDeliveryDateCalc = getMinDeliveryDateForMode({
            pickUpDate,
            pickUpTime,
            isExpressMode: this.props.isExpress,
            items: shoppingCart?.items || []
        });

        const isBeforeMinDate = date < minDeliveryDateCalc;
        const isNotBusinessDay = !(moment(`${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`, 'DD-MM-YYYY').isBusinessDay());

        // When express is off, allow the express minimum date calculated with the SAME rules path.
        const expressMinDate = !allLongProcessing
            ? getMinDeliveryDateForMode({
                pickUpDate,
                pickUpTime,
                isExpressMode: true,
                items: shoppingCart?.items || []
            })
            : null;
        const isExpressMinDate = expressMinDate && moment(date).startOf('day').isSame(moment(expressMinDate).startOf('day'), 'day');
        const allowExpressMinWhenOff = !this.props.isExpress && isExpressMinDate;

        return ((isBeforeMinDate && !allowExpressMinWhenOff) || isNotBusinessDay);
    };

    render() {
        const { t } = this.props;
        const { deliveryLocationConfirmed, pickupLocationConfirmed, shoppingCart, token, loading, isVip, showOnlyPayment, isAdmin } = this.props;
        const showExpressDebugControls =
            isAdmin &&
            new URLSearchParams(window.location.search).get('debug') === 'true' &&
            (this.getExpressPrice() > 0 || shoppingCart.items.length > 0);

        let adminTestDateTimeRaw = null;
        try {
            adminTestDateTimeRaw =
                isAdmin && typeof sessionStorage !== 'undefined'
                    ? sessionStorage.getItem(CHECKOUT_ADMIN_TEST_DATETIME_KEY)
                    : null;
        } catch (e) {
            adminTestDateTimeRaw = null;
        }
        let adminTestDateTimeInputValue = '';
        if (adminTestDateTimeRaw) {
            const parsed = new Date(adminTestDateTimeRaw);
            if (!isNaN(parsed.getTime())) {
                adminTestDateTimeInputValue = formatDateTimeLocalInputValue(parsed);
            }
        }
        const adminTestDateTimeDisplayValue =
            this.state.adminTestDateTimeDraft !== null
                ? this.state.adminTestDateTimeDraft
                : adminTestDateTimeInputValue;

        if (shoppingCart.items.length === 0) {
            return <Redirect to="/products" />
        }

        if (!token) {
            this.props.OnAuthModalToggle()
            return <Redirect to="/shopping-cart/page1" />
        }

        if (loading) {
            return <ProductLoader />
        }

        const { isCalendarOpen, currentFromToDateBeingEdited, pickUpDate, deliveryDate, modalDraftDate, modalDraftTime } = this.state;
        const calendarLocale = 'de-CH';
        const dateFormatOptions = { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'numeric', 
            day: 'numeric' 
        };
        // Date/time shown in the modal: draft when set, else real state (so modal displays selection before time slot click)
        const dateInModal = modalDraftDate ?? (currentFromToDateBeingEdited === 'pickUpDate' ? pickUpDate : deliveryDate);
        const realDate = currentFromToDateBeingEdited === 'pickUpDate' ? pickUpDate : deliveryDate;
        const realTime = currentFromToDateBeingEdited === 'pickUpDate' ? this.state.pickUpTime : this.state.deliveryTime;
        // Only show a time slot as active when it's the chosen time for the day currently shown: use draft time if set,
        // or real time only when the modal is showing the same day as real state (otherwise no slot active)
        const timeInModal = (modalDraftTime !== undefined && modalDraftTime !== '')
            ? modalDraftTime
            : (dateInModal && realDate && moment(dateInModal).isSame(moment(realDate), 'day') ? realTime : '');

        // Calculate max date for calendar (1 month from current date)
        // Use getCurrentDate() to allow date override for testing
        const currentDate = getCurrentDate();
        const currentMoment = moment(`${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`, 'DD-MM-YYYY');
        const maxDate = currentMoment.clone().add(1, 'months').toDate();

        // Saturday restrictions: disable late afternoon slots (15:00-17:00, 17:00-19:00) for the date shown in the modal
        const timeSlotDisabled = (currentFromToDateBeingEdited === 'pickUpDate' && dateInModal.getDay() === 6) ||
            (currentFromToDateBeingEdited === 'deliveryDate' && dateInModal.getDay() === 6);
        
        // Delivery constraints depend on the selected pickup day, not the day currently viewed in modal.
        const pickupIsToday = isToday(pickUpDate);
        const isEditingPickup = currentFromToDateBeingEdited === 'pickUpDate';
        const isEditingDelivery = currentFromToDateBeingEdited === 'deliveryDate';
        const isExpressForUi = isEditingDelivery
            ? shouldUseExpressForSelection({
                pickUpDate,
                pickUpTime: this.state.pickUpTime,
                deliveryDate: dateInModal,
                items: shoppingCart?.items || []
            })
            : this.props.isExpress;
        const sameDayAllowed = isSameDayPickupAllowed(isExpressForUi);
        
        // Helper function to check if a time slot should be disabled/hidden
        // Time slots are hidden (not just disabled) when this returns true
        // Rules:
        // - For pickup: morning slots hidden if pickup date is today AND express is enabled
        // - For delivery: morning slots hidden if pickup was same-day AND express is enabled
        // - For 24-hour delivery: on minimum date (pickup + 1 day), hide time slots before pickup time
        // - For 24-hour delivery: on dates AFTER minimum, show ALL time slots (return false)
        // Note: Uses this.state.deliveryDate directly to ensure we get the latest state value
        const isTimeSlotDisabled = (timeSlot) => {
            // TEMP: hide late afternoon slots on all days
            if (isSlotTemporarilyDisabled(timeSlot)) {
                return true;
            }
            // When express is off and pickup is today: show only afternoon slots (express-only); hide morning
            if (isEditingPickup && pickupIsToday && !this.props.isExpress) {
                return isMorningSlot(timeSlot);
            }
            // Express minimum-day rule: on the earliest express delivery day, hide time slots earlier than pickup.
            if (isEditingDelivery && isExpressForUi && isBeforePickupTimeOnExpressMinDay({
                pickUpDate: this.state.pickUpDate,
                pickUpTime: this.state.pickUpTime,
                deliveryDate: dateInModal,
                deliveryTime: timeSlot,
                items: shoppingCart?.items || []
            })) {
                return true;
            }
            
            // Disable past time slots when pickup is today (even if express is disabled, as a safety measure)
            if (isEditingPickup && pickupIsToday) {
                const currentHour = getCurrentHourZurich();
                const timeSlotHours = {
                    '07:00 - 09:00': 7,
                    '09:00 - 11:00': 9,
                    '11:00 - 13:00': 11,
                    '13:00 - 15:00': 13,
                    '15:00 - 17:00': 15,
                    '17:00 - 19:00': 17
                };
                const slotStartHour = timeSlotHours[timeSlot];
                // If the time slot has already started (current hour >= slot start hour), disable it
                if (slotStartHour !== undefined && currentHour >= slotStartHour) {
                    return true; // Time slot is in the past
                }
            }
            
            if (isMorningSlot(timeSlot)) {
                if (isEditingPickup && pickupIsToday && sameDayAllowed) {
                    return true; // Morning slots disabled for same-day pickup
                }
                if (isEditingDelivery && pickupIsToday && sameDayAllowed) {
                    // Morning slots disabled for delivery when pickup was same-day,
                    // but ONLY if delivery date is the minimum date (next business day)
                    // For delivery dates AFTER the minimum, all time slots are allowed
                    const currentPickupDate = this.state.pickUpDate;
                    const currentDeliveryDate = dateInModal;
                    
                    // Calculate minimum delivery date (next business day after pickup)
                    const pickupMoment = moment(currentPickupDate).startOf('day');
                    const minDeliveryDateMoment = getDeliveryDate(pickupMoment.toDate(), null, isExpressForUi);
                    const minDeliveryMoment = moment(minDeliveryDateMoment).startOf('day');
                    const deliveryMoment = moment(currentDeliveryDate).startOf('day');
                    const isMinimumDeliveryDate = deliveryMoment.isSame(minDeliveryMoment, 'day');
                    
                    // Only hide morning slots if delivery date is the minimum date
                    if (isMinimumDeliveryDate) {
                        return true; // Morning slots disabled for delivery when pickup was same-day and delivery is minimum date
                    }
                    // For delivery dates after minimum, show all time slots
                    return false;
                }
            }
            return false;
        };

        // Helper: pickup keeps express badges for express slots; delivery badges are hidden only
        // when the selected pickup slot is already an express slot.
        const allLongProcessingCart = allItemsAreLongProcessing(shoppingCart?.items || []);
        const expressMinDeliveryDate = !allLongProcessingCart ? getDeliveryDate(pickUpDate, null, true, shoppingCart.items) : null;
        const isDeliveryDateExpressMin = expressMinDeliveryDate && moment(dateInModal).startOf('day').isSame(moment(expressMinDeliveryDate).startOf('day'), 'day');
        const pickupSlotIsExpress = isExpressPickupSlot(pickUpDate, this.state.pickUpTime);
        const showPickupExpressOptionUi = isEditingPickup && pickupIsToday;
        const showDeliveryExpressOptionUi = isEditingDelivery && !pickupSlotIsExpress && isDeliveryDateExpressMin;
        const isExpressOnlyTimeSlot = (timeSlot) => (showPickupExpressOptionUi && !isMorningSlot(timeSlot)) || showDeliveryExpressOptionUi;
        const hasExpressOnlySlotsOnThisDay = showPickupExpressOptionUi || showDeliveryExpressOptionUi;

            
        return (
            <>
            <div className="checkout-out-page-wrapper">
                <AddressModal modalShowed={this.state.showModal} address={this.state.addressForEdit} modalToggle={this.modalToggleHandler} />

                <Modal isOpen={isCalendarOpen} toggle={this.toggleDateTimePicker} centered size="sm" >
                    <ModalHeader toggle={this.toggleDateTimePicker}>{t('cart.checkout.dateTime.title')}</ModalHeader>
                    <ModalBody>
                        <Calendar
                            value={(currentFromToDateBeingEdited === 'pickUpDate' ? (modalDraftDate ?? pickUpDate) : (modalDraftDate ?? deliveryDate))}
                            locale={calendarLocale}
                            onChange={this.onCalendarDateChange}
                            maxDate={maxDate}
                            tileDisabled={({ activeStartDate, date }) => currentFromToDateBeingEdited === 'pickUpDate' ? this.disabledPickupDates(activeStartDate, date) : this.disabledDeliveryDate(activeStartDate, date)}
                            tileClassName={({ date, view }) => this.getTileClassName({ date, view })}
                        />
                        {hasExpressOnlySlotsOnThisDay && (
                            <div className="calendar-express-info mt-3 mb-3 px-2">
                                <strong><SvgLightning /> EXPRESS</strong><FontAwesomeIcon className="ml-1" style={{ color: "#2a4a91" }} icon={faInfoCircle} id="express-hint" />
                                                <UncontrolledPopover trigger="hover" placement="left" target="express-hint">
                                                    <PopoverHeader>{t('cart.options.washAndIronExpress')}</PopoverHeader>
                                                    <PopoverBody>{t('cart.laundry.expressInfo')}</PopoverBody>
                                                </UncontrolledPopover><br/>
                                {t('cart.options.expressDescriptionShort')} {formatNumber(this.getExpressPrice())}
                            </div>
                        )}
                        <Row className='pb-2 mt-3'>
                            {!isTimeSlotDisabled('07:00 - 09:00') && (
                                <Col>
                                    <div className={isExpressOnlyTimeSlot('07:00 - 09:00') ? 'time-slot--express-option' : undefined}>
                                        <Button
                                            id="07:00 - 09:00"
                                            size='sm'
                                            block
                                            outline
                                            color="info"
                                            className="time-btn"
                                            active={timeInModal === '07:00 - 09:00'}
                                            onClick={this.setTime}
                                        >07:00 - 09:00{isExpressOnlyTimeSlot('07:00 - 09:00') && <span className="express-badge ml-1">⚡</span>}</Button>
                                    </div>
                                </Col>
                            )}
                            {!isTimeSlotDisabled('09:00 - 11:00') && (
                                <Col>
                                    <div className={isExpressOnlyTimeSlot('09:00 - 11:00') ? 'time-slot--express-option' : undefined}>
                                        <Button
                                            id='09:00 - 11:00'
                                            size='sm'
                                            block
                                            outline
                                            color="info"
                                            className="time-btn"
                                            active={timeInModal === '09:00 - 11:00'}
                                            onClick={this.setTime}
                                        >09:00 - 11:00{isExpressOnlyTimeSlot('09:00 - 11:00') && <span className="express-badge ml-1">⚡</span>}</Button>
                                    </div>
                                </Col>
                            )}
                        </Row>
                        <Row className='pb-2'>
                            {!isTimeSlotDisabled('11:00 - 13:00') && (
                                <Col>
                                    <div className={isExpressOnlyTimeSlot('11:00 - 13:00') ? 'time-slot--express-option' : undefined}>
                                        <Button
                                            id='11:00 - 13:00'
                                            size='sm'
                                            block
                                            outline
                                            color="info"
                                            className="time-btn"
                                            active={timeInModal === '11:00 - 13:00'}
                                            onClick={this.setTime}
                                        >11:00 - 13:00{isExpressOnlyTimeSlot('11:00 - 13:00') && <span className="express-badge ml-1">⚡</span>}</Button>
                                    </div>
                                </Col>
                            )}
                            {!isTimeSlotDisabled('13:00 - 15:00') && (
                                <Col>
                                    <div className={isExpressOnlyTimeSlot('13:00 - 15:00') ? 'time-slot--express-option' : undefined}>
                                        <Button
                                            id='13:00 - 15:00'
                                            size='sm'
                                            block
                                            outline
                                            color="info"
                                            className="time-btn"
                                            active={timeInModal === '13:00 - 15:00'}
                                            onClick={this.setTime}
                                        >13:00 - 15:00{isExpressOnlyTimeSlot('13:00 - 15:00') && <span className="express-badge ml-1">⚡</span>}</Button>
                                    </div>
                                </Col>
                            )}
                        </Row>
                        <Row className='pb-2'>
                            {!isTimeSlotDisabled('15:00 - 17:00') && !timeSlotDisabled && (
                                <Col>
                                    <div className={isExpressOnlyTimeSlot('15:00 - 17:00') ? 'time-slot--express-option' : undefined}>
                                        <Button
                                            id='15:00 - 17:00'
                                            size='sm'
                                            block
                                            outline
                                            color="info"
                                            className="time-btn"
                                            active={timeInModal === '15:00 - 17:00'}
                                            onClick={this.setTime}
                                        >15:00 - 17:00{isExpressOnlyTimeSlot('15:00 - 17:00') && <span className="express-badge ml-1">⚡</span>}</Button>
                                    </div>
                                </Col>
                            )}
                            {!isTimeSlotDisabled('17:00 - 19:00') && !timeSlotDisabled && (
                                <Col>
                                    <div className={isExpressOnlyTimeSlot('17:00 - 19:00') ? 'time-slot--express-option' : undefined}>
                                        <Button
                                            id='17:00 - 19:00'
                                            size='sm'
                                            block
                                            outline
                                            color="info"
                                            className="time-btn"
                                            active={timeInModal === '17:00 - 19:00'}
                                            onClick={this.setTime}
                                        >17:00 - 19:00{isExpressOnlyTimeSlot('17:00 - 19:00') && <span className="express-badge ml-1">⚡</span>}</Button>
                                    </div>
                                </Col>
                            )}
                        </Row>

                    </ModalBody>
                    <div style={{ alignSelf: 'center', textAlign: "center" }}>
                        <h5>{`${dateInModal.toLocaleString(calendarLocale, dateFormatOptions)} `}</h5>
                        <h5>{timeInModal}</h5>
                    </div>
                    <ModalFooter>
                        {/* <Button block size='sm' color="primary" onClick={this.onDateModalSubmit}>BESTÄTIGEN</Button>
                        <Button block size='sm' color="secondary" className="no-margin-top" onClick={this.toggle}>ABBRECHEN</Button> */}
                        <Button block size='sm' color="primary" className="no-margin-top" style={{ textTransform: "uppercase" }} onClick={this.onDateModalSubmit}>{t('cart.checkout.dateTime.close')}</Button>
                    </ModalFooter>
                </Modal>
                <Row>
                    <Col>
                    { showExpressDebugControls &&
                                <div className="express d-flex">
                                    <label class="toggle-wrapper">
                                        <input id="toggleExpress" type="checkbox" checked={this.props.isExpress} onChange={this.onChangeExpress} />
                                        <span class="slider"></span>
                                    </label>
                                    <label htmlFor="toggleExpress">
                                        <p>{t('cart.options.expressProcessing24h')} – {this.props.isExpress ? t('cart.options.expressEnabled') : t('cart.options.expressDisabled')}</p>
                                        <div class="">{t('cart.options.expressDescription')}<span class="hidden-mobile">&nbsp;{t('cart.options.washAndIron')}</span>: +{formatNumber(this.getExpressPrice())} <FontAwesomeIcon className="ml-1" style={{ color: "#2a4a91" }} icon={faInfoCircle} id="express-hint" />
                                                <UncontrolledPopover trigger="hover" placement="left" target="express-hint">
                                                    <PopoverHeader>{t('cart.options.washAndIronExpress')}</PopoverHeader>
                                                    <PopoverBody>{t('cart.laundry.expressInfo')}</PopoverBody>
                                                </UncontrolledPopover></div>
                                        <div class="d-flex">
                                            
                                           
                                        </div>
                                    </label>
                                </div>
                            }
                    </Col>
                </Row>
                <Row style={{ justifyContent: 'center' }}>
                    {
                        !showOnlyPayment &&
                        <>
                            <Col lg="4" style={{ marginBottom: "15px" }}>
                                <CheckoutPageStepBox
                                    icon={require("../../img/how-it-works/24flipped.png")}
                                    headerText={t('cart.checkout.pickup.title')}
                                    confirmed={this.props.pickupLocationConfirmed}
                                    editVisible={!this.props.pickupLocationConfirmed}
                                    confirm={(value) => this.props.confirmPickUpLocation(value, isVip)}
                                    needsConfirmation={false}
                                    address={this.props.defaultPickupAddress}
                                    editClick={(e) => this.editClickHandler(e, this.props.defaultPickupAddress)}
                                    updateFromToDateTime={(e) => this.onUpdateFromToDate('pickUpDate', e)}
                                    date={`${this.state.pickUpDate.toLocaleString(calendarLocale, dateFormatOptions)}`}
                                    time={this.state.pickUpTime}
                                    disabledButton={false}
                                    confirmWithoutAddress={() => this.props.confirmWithoutAddress(1)}
                                    noAddress={shoppingCart.selfPickUp}
                                    noAddressButtonText={t('cart.checkout.pickup.inStore')}
                                    stepNumber={1}
                                    earliestDate={(() => {
                                        const earliest = this.getEarliestPickupNormal();
                                        return earliest.date.toLocaleString(calendarLocale, dateFormatOptions);
                                    })()}
                                    earliestTime={this.getEarliestPickupNormal().time}
                                    earliestWithExpressDate={(() => {
                                        const earliest = this.getEarliestPickupWithExpress();
                                        return earliest.date.toLocaleString(calendarLocale, dateFormatOptions);
                                    })()}
                                    earliestWithExpressTime={this.getEarliestPickupWithExpress().time}
                                   
                                />
                            </Col>
                            <Col lg="4" style={{ marginBottom: "15px" }}>
                                <CheckoutPageStepBox
                                    icon={require("../../img/how-it-works/24.png")}
                                    headerText={t('cart.checkout.delivery.title')}
                                    editVisible={this.props.pickupLocationConfirmed && !this.props.deliveryLocationConfirmed}
                                    needsConfirmation={!this.props.pickupLocationConfirmed}
                                    confirmed={this.props.deliveryLocationConfirmed}
                                    confirm={(value) => this.props.confirmDeliveryLocation(value, isVip)}
                                    address={this.props.defaultDeliveryAddress}
                                    editClick={(e) => this.editClickHandler(e, this.props.defaultDeliveryAddress)}
                                    updateFromToDateTime={(e) => this.onUpdateFromToDate('deliveryDate', e)}
                                    date={`${this.state.deliveryDate.toLocaleString(calendarLocale, dateFormatOptions)}`}
                                    time={this.state.deliveryTime}
                                    disabledButton={!this.props.pickupLocationConfirmed}
                                    confirmWithoutAddress={() => this.props.confirmWithoutAddress(2)}
                                    noAddress={shoppingCart.selfDelivery}
                                    noAddressButtonText={t('cart.checkout.delivery.inStore')}
                                    expressPrice={formatNumber(this.getExpressPrice())}
                                    onToggleExpress={this.onChangeExpress}
                                    isExpress={this.props.isExpress}
                                    stepNumber={2}
                                    hasLongProcessingItems={hasLongProcessingItems(shoppingCart.items)}
                                    allItemsAreLongProcessing={allItemsAreLongProcessing(shoppingCart.items)}
                                    earliestDate={(() => {
                                        const earliest = this.getEarliestDeliveryNormal();
                                        return earliest.date.toLocaleString(calendarLocale, dateFormatOptions);
                                    })()}
                                    earliestTime={this.getEarliestDeliveryNormal().time}
                                    earliestWithExpressDate={(() => {
                                        const earliest = this.getEarliestDeliveryWithExpress();
                                        return earliest.date.toLocaleString(calendarLocale, dateFormatOptions);
                                    })()}
                                    earliestWithExpressTime={this.getEarliestDeliveryWithExpress().time}
                                />
                                
                            </Col>
                        </>
                    }

                    <Col lg="4" style={{ marginBottom: "15px" }}>
                        <Card className={`checkout-step-box ${((!pickupLocationConfirmed || !deliveryLocationConfirmed) && !showOnlyPayment) ? 'needsConformation' : ''}`}>
                        {
                            ((!pickupLocationConfirmed || !deliveryLocationConfirmed) && !showOnlyPayment) &&
                            <>
                                <span className="checkout-step-box-number"><img alt="" style={{ position: 'relative', left: '12px' }} src={require('../../img/how-it-works/1.png')} /></span>
                                <span className="checkout-step-box-title">3. {t('cart.checkout.summary.steptitle')}</span>
                            </>
                        }
                            <CardHeader>
                                <img alt="" src={require('../../img/how-it-works/1.png')} />
                                {t('cart.checkout.summary.title')}
                            </CardHeader>
                            <CardBody>
                                <ShoppingCartTotals />
                            </CardBody>
                            <CardFooter>
                                <Button
                                    color="info"
                                    className={`checkout-btn ${this.state.loading ? ' loading-btn' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        
                                        this.handleOrderSubmit()
                                    }}
                                    disabled={((!pickupLocationConfirmed || !deliveryLocationConfirmed) && !showOnlyPayment)}
                                >
                                    { 
                                        invoiceOnly(this.props.userEmail) && 
                                        <span>{t('cart.checkout.payment.confirmWithoutPay')}</span>
                                    }
                                    { 
                                        !invoiceOnly(this.props.userEmail) && 
                                        <span>{t('cart.checkout.payment.confirm')}</span>
                                    }
                                </Button>
                            </CardFooter>
                        </Card>

                    </Col>
                </Row>
            </div>
            {isAdmin && (
                <div
                    className="checkout-admin-test-datetime border-top mt-3 pt-3 pb-4 px-3 small text-center"
                    style={{ background: '#f8f9fa'}}
                >
                    <label htmlFor="checkout-admin-test-datetime-input" className="mb-2 d-block font-weight-bold">
                        Admin: simulated date and time
                    </label>
                    <p className="text-muted mb-2" style={{ maxWidth: '42rem', margin: '0 auto' }}>
                        Set date and time, then click outside the field to apply (page reloads).
                    </p>
                    <div className="d-flex flex-wrap align-items-center justify-content-center">
                        <input
                            id="checkout-admin-test-datetime-input"
                            type="datetime-local"
                            className="form-control form-control-sm"
                            style={{ maxWidth: '280px' }}
                            value={adminTestDateTimeDisplayValue}
                            onFocus={this.handleAdminTestDateTimeFocus}
                            onChange={this.handleAdminTestDateTimeInputChange}
                            onBlur={this.handleAdminTestDateTimeBlur}
                        />
                        {adminTestDateTimeRaw && (
                            <Button
                                type="button"
                                color="secondary"
                                size="sm"
                                className="ml-sm-2 mt-2 mt-sm-0"
                                onClick={this.clearAdminTestDateTime}
                            >
                                Use live time
                            </Button>
                        )}
                    </div>
                </div>
            )}
            </>
        );
    }
}

const mapStateToProps = state => {
    // Calculate effective minDeliveryDate based on longProcessing and express status
    const baseMinDeliveryDate = Math.max(...state.shoppingCart.deliveryDatesAvailable);
    const hasLongProcessing = hasLongProcessingItems(state.shoppingCart.items || []);
    const allLongProcessing = allItemsAreLongProcessing(state.shoppingCart.items || []);
    const isExpress = state.shoppingCart.isExpress;
    
    // If ALL items are longProcessing, use 14 days for both regular and express
    // If NOT express and only SOME items are longProcessing (mixed), use 2 days (normal)
    // If express and not all longProcessing, use 1 day (normal)
    // Otherwise use the base value
    let effectiveMinDeliveryDate = baseMinDeliveryDate;
    if (allLongProcessing) {
        effectiveMinDeliveryDate = 14; // ALL longProcessing: 14 days for both regular and express
    } else if (isExpress) {
        effectiveMinDeliveryDate = 1; // Express with mixed/no longProcessing: 1 day (warning shown if hasLongProcessing)
    } else {
        effectiveMinDeliveryDate = 2; // Regular with mixed or no longProcessing: 2 days (normal)
    }
    
    return {
        loading: state.addresses.loading,
        pickupLocationConfirmed: state.shoppingCart.pickupLocationConfirmed,
        deliveryLocationConfirmed: state.shoppingCart.deliveryLocationConfirmed,
        shoppingCart: state.shoppingCart,
        token: state.auth.token,
        userEmail: state.auth.userEmail,
        isVip: state.auth.isVip,
        isAdmin: state.auth.isAdmin,
        defaultPickupAddress: getDefaultPickupAddress(state),
        defaultDeliveryAddress: getDefaultDeliveryAddress(state),
        getBillingAddress: getBillingAddress(state),
        showOnlyPayment: showOnlyPaymentOnCheckout(state),
        minDeliveryDate: effectiveMinDeliveryDate,
        orderId: state.shoppingCart.orderId,
        paymentLink: state.shoppingCart.paymentLink,
        isExpress: isExpress,
        expressPrice: state.shoppingCart.expressPrice,
        deliveryDatesAvailable: state.shoppingCart.deliveryDatesAvailable
    };
};

const mapDispatchToProps = dispatch => ({
    removeAllProductsFromShoppingCart: () => dispatch(removeAllProductsFromShoppingCart()),
    confirmPickUpLocation: (value, isVip) => dispatch(confirmPickUpLocation(value, isVip)),
    confirmDeliveryLocation: (value, isVip) => dispatch(confirmDeliveryLocation(value, isVip)),
    onSubmitOrder: (payload) => dispatch(submitOrder(payload)),
    onSubmitOrderAndGoToDetails: (payload) => dispatch(submitOrderAndGoToDetails(payload)),
    OnGetAddresses: (token) => dispatch(getAddresses(token)),
    confirmWithoutAddress: (addressType) => dispatch(confirmWithoutAddress(addressType)),
    OnAuthModalToggle: () => dispatch(authModalToggle()),
    onToggleExpress: (source) => dispatch(toggleExpress(source))
});


export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(CheckoutPage));